import React, {useEffect, useMemo, useRef} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, mb, mh, onDesktop, p, ph, pr, pv} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons-module";
import {useModal} from "@pg-design/modal";
import {Popover} from "@pg-design/notifications";
import {Slider} from "@pg-design/slider";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useIsMounted, useMouseSwipe} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {useGetReelsListQuery} from "../../api/get_reels_list";
import {reelsFullWithSliderSettings} from "../../constants/reels_full_with_slider_settings";
import {useReelUrlSync} from "../../hooks/use_reel_url_sync";
import {useReelsSeen} from "../../hooks/use_reels_seen";
import {useReelsSortByPlays} from "../../hooks/use_reels_sort_by_plays";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelListItem} from "../../types/IReelListItem";
import {ReelModal} from "../modal/ReelModal";
import {Reel} from "./reel/Reel";
import {ReelSkeletonLoader} from "./reel/ReelSkeletonLoader";

interface IProps {
    selectedRegions?: ISelectedRegion[];
    onAfterModalClose?: () => void;
}

export const ReelList = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const sliderWrapperRef = useRef<HTMLDivElement>(null);
    const lastMouseSliderWrapperSwipeDelta = useRef(0);
    const selectedRegionIds = useMemo(() => props.selectedRegions?.map((region) => region.id), [props.selectedRegions]);
    const {data, isLoading} = useGetReelsListQuery({regionIds: selectedRegionIds});
    const hasBeenInitialized = useRef(false);
    const {syncReelUrl, hasUrlWithReelId, getReelIdFromUrl} = useReelUrlSync();
    const modal = useModal<{currentReelId?: number}>({
        onAfterClose: () => {
            syncReelUrl();
            props.onAfterModalClose?.();
        }
    });

    const reelsCount = data?.count || 0;
    const {reels, hasStatsBeenApplied} = useReelsSortByPlays({reels: data?.results}, [data?.results, modal.isModalRendered]);
    const {reelsSeen, setReelsSeen} = useReelsSeen();

    useEffect(() => {
        if (reelsCount > 0) {
            reelsAnalytics.gtm.reelsShownEvent();
        }
    }, [reelsCount > 0]);

    if (!isLoading && reelsCount === 0) {
        return null;
    }

    const onShowcaseClose = () => {
        setReelsSeen(true);
    };

    const handleReelClick = (reel: IReelListItem) => {
        if (lastMouseSliderWrapperSwipeDelta.current > 10) {
            // Prevent click event after swipe
            return;
        }
        onShowcaseClose();
        reelsAnalytics.gtm.reelClickEvent({reels_name: reel.title, reels_developer: reel.creator.name});
        modal.openModal({currentReelId: reel.id});
    };

    const normalizeReelsFullWithSliderSettings = useMemo(() => {
        return {
            ...reelsFullWithSliderSettings,
            responsive: reelsFullWithSliderSettings.responsive.map(({breakpoint, settings}) => ({
                breakpoint,
                settings: {
                    ...settings,
                    infinite: reelsCount > settings.slidesToShow
                }
            }))
        };
    }, [reelsCount]);

    useEffect(() => {
        if (hasBeenInitialized.current) {
            return;
        }

        if (!isLoading && reelsCount > 0 && hasUrlWithReelId) {
            const currentReelId = getReelIdFromUrl();
            hasBeenInitialized.current = true;

            if (currentReelId) {
                modal.openModal({currentReelId});
            }
        }
    }, [isLoading, reelsCount, hasUrlWithReelId, getReelIdFromUrl]);

    const onMouseSwipe = ({delta}: {delta: number}) => {
        lastMouseSliderWrapperSwipeDelta.current = delta;
    };

    useMouseSwipe({
        element: sliderWrapperRef.current,
        onSwipeLeft: onMouseSwipe,
        onSwipeDown: onMouseSwipe,
        onSwipeRight: onMouseSwipe,
        onSwipeUp: onMouseSwipe
    });

    return (
        <>
            <div css={[overlay, display(reelsSeen ? "none" : "block")]} />
            <Popover
                isActive={!reelsSeen}
                openOnMount
                onPopoverClose={onShowcaseClose}
                body={
                    <div>
                        <div css={popupTextSection}>
                            <Text variant="body_copy_1" mb="1.6rem">
                                Poznaj nasz nowy sposób prezentacji treści w formie kilkunastosekundowych filmów. Dowiedz się, na jakie inwestycje i trendy
                                warto zwrócić uwagę w danej lokalizacji.
                            </Text>
                            <CloseIcon
                                wrapperSize="2"
                                size="2"
                                wrapperColor="transparent"
                                fill={getThemeVariable("colors.secondary")}
                                onClick={onShowcaseClose}
                                css={{cursor: "pointer"}}
                            />
                        </div>
                        <Button variant="filled_primary" size="x-small" onClick={onShowcaseClose}>
                            Ok, dziękuje
                        </Button>
                    </div>
                }
                bodyStyle={popoverStyle}
                wrapperStyle={popoverWrapper}
                popoverPlace="top"
                toggleOnClickOutside
                appendTo="parent"
            >
                <ReelsWrapper firstTimeShow={!reelsSeen}>
                    <Text variant="headline_6" css={headerStyle}>
                        Odkrywaj trendy, promocje, atuty inwestycji
                    </Text>
                    <SliderWrapper ref={sliderWrapperRef} isMounted={isMounted}>
                        {isLoading ? (
                            <div css={skeletonLoaderWrapperStyle}>
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                            </div>
                        ) : (
                            <Slider settings={normalizeReelsFullWithSliderSettings} slickArrowStyle={sliderSlickArrowStyle}>
                                {reels.map((reel, index) =>
                                    hasStatsBeenApplied ? (
                                        <Reel
                                            reelData={reel}
                                            onClick={(reel) => handleReelClick(reel)}
                                            key={reel.id}
                                            size={isMobile ? "S" : "M"}
                                            css={reelStyle}
                                            forceHover={index === 0 && !reelsSeen}
                                        />
                                    ) : (
                                        <ReelSkeletonLoader key={reel.id} size={isMobile ? "S" : "M"} css={reelStyle} />
                                    )
                                )}
                            </Slider>
                        )}
                    </SliderWrapper>
                </ReelsWrapper>
            </Popover>

            <ReelModal modalInstance={modal} reels={reels} />
        </>
    );
};

const ReelsWrapper = styled.div<{firstTimeShow: boolean}>`
    position: relative;
    background-color: ${(props) => (props.firstTimeShow ? "#fff" : "unset")};
    z-index: ${(props) => (props.firstTimeShow ? 2010 : 1)};
    ${ph(1.5)};
    ${pv(1.5)};
    ${mb(3)};

    margin-left: -${calculateRemSize(1.5)};
    margin-right: -${calculateRemSize(1.5)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${(props) => ph(props.firstTimeShow ? 4 : 0)};
        margin-left: -${calculateRemSize(4)};
        margin-right: -${calculateRemSize(4)};
        ${(props) => (props.firstTimeShow ? "" : mh("auto"))};
        ${mb(6)};
    }
`;

const overlay = css`
    background-color: ${getThemeVariable("colors.secondary")};
    opacity: 0.75;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2002;
`;

const popupTextSection = css`
    display: flex;
    justify-content: flex-start;
    align-content: space-between;
    flex-direction: row;
    width: 100%;
`;

const popoverStyle = css`
    max-width: unset !important;
    background-color: #fff;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};
    }
`;

const popoverWrapper = css`
    max-width: 640px !important;
    ${mh(1.5)};
`;

const headerStyle = css`
    ${mb(2)};
`;

const SliderWrapper = styled.div<{isMounted: boolean}>`
    position: relative;
    width: 100%;

    & > div {
        ${p(0)};
    }

    .slick-prev {
        left: -${calculateRemSize(2)};
    }

    .slick-next {
        right: ${calculateRemSize(1.5)};
    }

    .slick-arrow {
        z-index: 1;
        ${({isMounted}) => !isMounted && "display: none !important;"};
    }

    .slick-list {
        ${({isMounted}) => !isMounted && "overflow: hidden;"}
    }

    .slick-track {
        margin-left: 0;
    }

    .slick-slide {
        width: auto !important;
        ${pr(1.5)};

        ${onDesktop(css`
            ${pr(3)};
        `)};
    }
`;
// NEXT STAGE OF REELS
// const showMoreStyle = (theme: Theme) => css`
//     ${p(1, 0)};
//     text-decoration: underline;
//     align-self: flex-end;
//     line-height: 2rem;
//
//     ${onDesktop(css`
//         line-height: 2rem;
//     `)};
//
//     &:hover {
//         color: ${theme.colors.highlight};
//     }
// `;

const skeletonLoaderWrapperStyle = css`
    display: flex;
    flex-direction: row;
    gap: ${calculateRemSize(1.5)};

    ${onDesktop(css`
        gap: ${calculateRemSize(3)};
    `)};
`;

const reelStyle = css`
    flex: 0 0 auto;
`;

const sliderSlickArrowStyle = css`
    &.slick-arrow {
        ${p(0)};
        top: 50%;
        transform: translateY(-${calculateRemSize(3)});
    }

    &.slick-next {
        right: -${calculateRemSize(2)} !important;
    }
`;
import React, {useEffect, useMemo, useRef} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, mb, mh, onDesktop, p, ph, pr, pv} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons-module";
import {useModal} from "@pg-design/modal";
import {Popover} from "@pg-design/notifications";
import {Slider} from "@pg-design/slider";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useIsMounted, useMouseSwipe} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {useGetReelsListQuery} from "../../api/get_reels_list";
import {reelsFullWithSliderSettings} from "../../constants/reels_full_with_slider_settings";
import {useReelUrlSync} from "../../hooks/use_reel_url_sync";
import {useReelsSeen} from "../../hooks/use_reels_seen";
import {useReelsSortByPlays} from "../../hooks/use_reels_sort_by_plays";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelListItem} from "../../types/IReelListItem";
import {ReelModal} from "../modal/ReelModal";
import {Reel} from "./reel/Reel";
import {ReelSkeletonLoader} from "./reel/ReelSkeletonLoader";

interface IProps {
    selectedRegions?: ISelectedRegion[];
    onAfterModalClose?: () => void;
}

export const ReelList = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const sliderWrapperRef = useRef<HTMLDivElement>(null);
    const lastMouseSliderWrapperSwipeDelta = useRef(0);
    const selectedRegionIds = useMemo(() => props.selectedRegions?.map((region) => region.id), [props.selectedRegions]);
    const {data, isLoading} = useGetReelsListQuery({regionIds: selectedRegionIds});
    const hasBeenInitialized = useRef(false);
    const {syncReelUrl, hasUrlWithReelId, getReelIdFromUrl} = useReelUrlSync();
    const modal = useModal<{currentReelId?: number}>({
        onAfterClose: () => {
            syncReelUrl();
            props.onAfterModalClose?.();
        }
    });

    const reelsCount = data?.count || 0;
    const {reels, hasStatsBeenApplied} = useReelsSortByPlays({reels: data?.results}, [data?.results, modal.isModalRendered]);
    const {reelsSeen, setReelsSeen} = useReelsSeen();

    useEffect(() => {
        if (reelsCount > 0) {
            reelsAnalytics.gtm.reelsShownEvent();
        }
    }, [reelsCount > 0]);

    if (!isLoading && reelsCount === 0) {
        return null;
    }

    const onShowcaseClose = () => {
        setReelsSeen(true);
    };

    const handleReelClick = (reel: IReelListItem) => {
        if (lastMouseSliderWrapperSwipeDelta.current > 10) {
            // Prevent click event after swipe
            return;
        }
        onShowcaseClose();
        reelsAnalytics.gtm.reelClickEvent({reels_name: reel.title, reels_developer: reel.creator.name});
        modal.openModal({currentReelId: reel.id});
    };

    const normalizeReelsFullWithSliderSettings = useMemo(() => {
        return {
            ...reelsFullWithSliderSettings,
            responsive: reelsFullWithSliderSettings.responsive.map(({breakpoint, settings}) => ({
                breakpoint,
                settings: {
                    ...settings,
                    infinite: reelsCount > settings.slidesToShow
                }
            }))
        };
    }, [reelsCount]);

    useEffect(() => {
        if (hasBeenInitialized.current) {
            return;
        }

        if (!isLoading && reelsCount > 0 && hasUrlWithReelId) {
            const currentReelId = getReelIdFromUrl();
            hasBeenInitialized.current = true;

            if (currentReelId) {
                modal.openModal({currentReelId});
            }
        }
    }, [isLoading, reelsCount, hasUrlWithReelId, getReelIdFromUrl]);

    const onMouseSwipe = ({delta}: {delta: number}) => {
        lastMouseSliderWrapperSwipeDelta.current = delta;
    };

    useMouseSwipe({
        element: sliderWrapperRef.current,
        onSwipeLeft: onMouseSwipe,
        onSwipeDown: onMouseSwipe,
        onSwipeRight: onMouseSwipe,
        onSwipeUp: onMouseSwipe
    });

    return (
        <>
            <div css={[overlay, display(reelsSeen ? "none" : "block")]} />
            <Popover
                isActive={!reelsSeen}
                openOnMount
                onPopoverClose={onShowcaseClose}
                body={
                    <div>
                        <div css={popupTextSection}>
                            <Text variant="body_copy_1" mb="1.6rem">
                                Poznaj nasz nowy sposób prezentacji treści w formie kilkunastosekundowych filmów. Dowiedz się, na jakie inwestycje i trendy
                                warto zwrócić uwagę w danej lokalizacji.
                            </Text>
                            <CloseIcon
                                wrapperSize="2"
                                size="2"
                                wrapperColor="transparent"
                                fill={getThemeVariable("colors.secondary")}
                                onClick={onShowcaseClose}
                                css={{cursor: "pointer"}}
                            />
                        </div>
                        <Button variant="filled_primary" size="x-small" onClick={onShowcaseClose}>
                            Ok, dziękuje
                        </Button>
                    </div>
                }
                bodyStyle={popoverStyle}
                wrapperStyle={popoverWrapper}
                popoverPlace="top"
                toggleOnClickOutside
                appendTo="parent"
            >
                <ReelsWrapper firstTimeShow={!reelsSeen}>
                    <Text variant="headline_6" css={headerStyle}>
                        Odkrywaj trendy, promocje, atuty inwestycji
                    </Text>
                    <SliderWrapper ref={sliderWrapperRef} isMounted={isMounted}>
                        {isLoading ? (
                            <div css={skeletonLoaderWrapperStyle}>
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                                <ReelSkeletonLoader size={isMobile ? "S" : "M"} css={reelStyle} />
                            </div>
                        ) : (
                            <Slider settings={normalizeReelsFullWithSliderSettings} slickArrowStyle={sliderSlickArrowStyle}>
                                {reels.map((reel, index) =>
                                    hasStatsBeenApplied ? (
                                        <Reel
                                            reelData={reel}
                                            onClick={(reel) => handleReelClick(reel)}
                                            key={reel.id}
                                            size={isMobile ? "S" : "M"}
                                            css={reelStyle}
                                            forceHover={index === 0 && !reelsSeen}
                                        />
                                    ) : (
                                        <ReelSkeletonLoader key={reel.id} size={isMobile ? "S" : "M"} css={reelStyle} />
                                    )
                                )}
                            </Slider>
                        )}
                    </SliderWrapper>
                </ReelsWrapper>
            </Popover>

            <ReelModal modalInstance={modal} reels={reels} />
        </>
    );
};

const ReelsWrapper = styled.div<{firstTimeShow: boolean}>`
    position: relative;
    background-color: ${(props) => (props.firstTimeShow ? "#fff" : "unset")};
    z-index: ${(props) => (props.firstTimeShow ? 2010 : 1)};
    ${ph(1.5)};
    ${pv(1.5)};
    ${mb(3)};

    margin-left: -${calculateRemSize(1.5)};
    margin-right: -${calculateRemSize(1.5)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${(props) => ph(props.firstTimeShow ? 4 : 0)};
        margin-left: -${calculateRemSize(4)};
        margin-right: -${calculateRemSize(4)};
        ${(props) => (props.firstTimeShow ? "" : mh("auto"))};
        ${mb(6)};
    }
`;

const overlay = css`
    background-color: ${getThemeVariable("colors.secondary")};
    opacity: 0.75;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2002;
`;

const popupTextSection = css`
    display: flex;
    justify-content: flex-start;
    align-content: space-between;
    flex-direction: row;
    width: 100%;
`;

const popoverStyle = css`
    max-width: unset !important;
    background-color: #fff;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};
    }
`;

const popoverWrapper = css`
    max-width: 640px !important;
    ${mh(1.5)};
`;

const headerStyle = css`
    ${mb(2)};
`;

const SliderWrapper = styled.div<{isMounted: boolean}>`
    position: relative;
    width: 100%;

    & > div {
        ${p(0)};
    }

    .slick-prev {
        left: -${calculateRemSize(2)};
    }

    .slick-next {
        right: ${calculateRemSize(1.5)};
    }

    .slick-arrow {
        z-index: 1;
        ${({isMounted}) => !isMounted && "display: none !important;"};
    }

    .slick-list {
        ${({isMounted}) => !isMounted && "overflow: hidden;"}
    }

    .slick-track {
        margin-left: 0;
    }

    .slick-slide {
        width: auto !important;
        ${pr(1.5)};

        ${onDesktop(css`
            ${pr(3)};
        `)};
    }
`;
// NEXT STAGE OF REELS
// const showMoreStyle = (theme: Theme) => css`
//     ${p(1, 0)};
//     text-decoration: underline;
//     align-self: flex-end;
//     line-height: 2rem;
//
//     ${onDesktop(css`
//         line-height: 2rem;
//     `)};
//
//     &:hover {
//         color: ${theme.colors.highlight};
//     }
// `;

const skeletonLoaderWrapperStyle = css`
    display: flex;
    flex-direction: row;
    gap: ${calculateRemSize(1.5)};

    ${onDesktop(css`
        gap: ${calculateRemSize(3)};
    `)};
`;

const reelStyle = css`
    flex: 0 0 auto;
`;

const sliderSlickArrowStyle = css`
    &.slick-arrow {
        ${p(0)};
        top: 50%;
        transform: translateY(-${calculateRemSize(3)});
    }

    &.slick-next {
        right: -${calculateRemSize(2)} !important;
    }
`;
